import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { getApiBase } from "../apiBase";
import { ConfessionCard } from "./ConfessionCard";
import { ConfessionReviewModal } from "./ConfessionReviewModal";
import "./FamilyConfessionsPanel.css";

const API_BASE = getApiBase();

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) {
    return `${value} B`;
  }
  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

async function sha256Hex(blob) {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  const bytes = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function toSafeFileStem(value) {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || "confession";
}

function formatTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatMonthYear(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
}

function formatDayLabel(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "";
  }
  const wholeSeconds = Math.round(seconds);
  const mins = Math.floor(wholeSeconds / 60);
  const secs = wholeSeconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}:${String(remMins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

function groupClipsByMonth(items) {
  const sorted = [...items].sort((a, b) => {
    const aTime = new Date(a.sharedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.sharedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  const months = [];
  const monthMap = new Map();

  sorted.forEach((clip) => {
    const dateValue = clip.sharedAt || clip.createdAt || "";
    const monthKey = formatMonthYear(dateValue) || "Unknown month";
    let monthGroup = monthMap.get(monthKey);
    if (!monthGroup) {
      monthGroup = { monthKey, items: [] };
      monthMap.set(monthKey, monthGroup);
      months.push(monthGroup);
    }
    monthGroup.items.push(clip);
  });

  return months;
}

function LiveCameraPreview({ stream, active }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }
    video.srcObject = stream || null;
    return () => {
      video.srcObject = null;
    };
  }, [stream]);

  return (
    <div className="confession-preview-shell">
      {active ? (
        <video
          ref={videoRef}
          className="confession-preview-video"
          autoPlay
          muted
          playsInline
          aria-label="Live camera preview"
        />
      ) : (
        <div className="confession-preview-placeholder" aria-hidden="true">
          <div className="confession-preview-icon">🎥</div>
          <strong>Record a private message for someone you care about</strong>
          <p>
            Your message stays private and is only released based on your instructions.
          </p>
        </div>
      )}
    </div>
  );
}

export function FamilyConfessionsPanel({
  authToken,
  recipientOptions = [],
  onOpenLinkedJournal = null,
  locale = "en",
}) {
  const isFrench = String(locale || "").toLowerCase().startsWith("fr");
  const copy = isFrench
    ? {
        panelTitle: "Enregistrez des messages prives pour les personnes qui comptent le plus.",
        panelSubtitle:
          "Passez en revue les messages prives que vous avez enregistres pour des personnes de confiance. Vous controlez qui les recoit et quand ils peuvent etre diffuses.",
        yourPrivateTitle: "Vos messages prives pour les personnes qui comptent le plus",
        yourPrivateSubtitle:
          "Passez en revue les messages prives que vous avez enregistres pour des personnes de confiance. Vous controlez qui les recoit et quand ils peuvent etre diffuses.",
        deliveredTitle: "Envoyes par vous",
        deliveredSubtitle: "Messages que vous avez choisi de partager immediatement.",
        sharedTitle: "Partage avec vous",
        sharedSubtitle: "Messages prives qu’une personne a partages directement avec vous.",
        noShared: "Aucun message prive n’a encore ete partage avec vous.",
        refreshJournals: "Actualiser les journaux",
        refreshSavedMessages: "Actualiser les messages enregistres",
      }
    : {
        panelTitle: "Record private messages for the people who matter most.",
        panelSubtitle:
          "Review private messages you have saved for trusted people. You control who receives each message and when it can be delivered.",
        yourPrivateTitle: "Your private messages for the people who matter most",
        yourPrivateSubtitle:
          "Review private messages you have saved for trusted people. You control who receives each message and when it can be delivered.",
        deliveredTitle: "Delivered by you",
        deliveredSubtitle: "Messages you chose to share immediately.",
        sharedTitle: "Shared with you",
        sharedSubtitle: "Private messages someone has shared directly with you.",
        noShared: "No private messages have been shared with you yet.",
        refreshJournals: "Refresh journals",
        refreshSavedMessages: "Refresh saved messages",
      };
  const [localError, setLocalError] = useState("");
  const [clips, setClips] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState("all-family");
  const [linkedJournalId, setLinkedJournalId] = useState("none");
  const [entryTitle, setEntryTitle] = useState("");
  const [contextNote, setContextNote] = useState("");
  const [recipientAfterGone, setRecipientAfterGone] = useState(true);
  const [journals, setJournals] = useState([]);
  const [loadingJournals, setLoadingJournals] = useState(false);
  const [loadingClips, setLoadingClips] = useState(false);
  const [savingClip, setSavingClip] = useState(false);
  const [uploadPlanNotice, setUploadPlanNotice] = useState("");
  const [activeClip, setActiveClip] = useState(null);
  const [clipDurations, setClipDurations] = useState({});
  const [retryingClipId, setRetryingClipId] = useState("");
  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
    previewStream,
    error: recorderError,
  } = useReactMediaRecorder({
    audio: true,
    video: true,
    askPermissionOnMount: false,
    blobPropertyBag: { type: "video/webm" },
  });

  const isRecording = status === "recording";
  const isStarting = status === "acquiring_media";
  const isStopping = status === "stopping";

  const startButtonLabel = isRecording
    ? "Recording…"
    : isStarting
      ? "Starting…"
      : isStopping
        ? "Stopping…"
        : "Start recording";

  const recipientLabelById = useMemo(() => {
    const map = new Map();
    recipientOptions.forEach((item) => {
      map.set(String(item.id), `${item.name} (${item.relation})`);
      if (item.backendNodeId) {
        map.set(String(item.backendNodeId), `${item.name} (${item.relation})`);
      }
    });
    return map;
  }, [recipientOptions]);

  const linkedJournal = useMemo(
    () => journals.find((item) => item.journalId === linkedJournalId) || null,
    [journals, linkedJournalId]
  );

  const savedThisYearCount = useMemo(() => {
    const year = new Date().getFullYear();
    return clips.filter((clip) => {
      const date = new Date(clip.createdAt || "");
      return !Number.isNaN(date.getTime()) && date.getFullYear() === year;
    }).length;
  }, [clips]);

  const displayClips = useMemo(
    () =>
      clips.map((clip) => ({
        ...clip,
        displayDateLabel: formatDayLabel(clip.createdAt),
        displaySavedAt: formatTime(clip.createdAt),
        sizeLabel: formatBytes(clip.sizeBytes),
        durationLabel: formatDuration(clipDurations[clip.confessionId]),
      })),
    [clipDurations, clips]
  );

  const displaySharedClips = useMemo(
    () =>
      sharedWithMe.map((clip) => ({
        ...clip,
        displayDateLabel: formatDayLabel(clip.sharedAt || clip.createdAt),
        displaySavedAt: formatTime(clip.sharedAt || clip.createdAt),
        sizeLabel: formatBytes(clip.sizeBytes),
        durationLabel: formatDuration(clipDurations[clip.confessionId]),
      })),
    [clipDurations, sharedWithMe]
  );

  const privateDisplayClips = useMemo(
    () => displayClips.filter((clip) => clip.deliveryStatus !== "DELIVERED_NOW"),
    [displayClips]
  );

  const deliveredDisplayClips = useMemo(
    () => displayClips.filter((clip) => clip.deliveryStatus === "DELIVERED_NOW"),
    [displayClips]
  );

  const groupedPrivateClips = useMemo(
    () => groupClipsByMonth(privateDisplayClips),
    [privateDisplayClips]
  );
  const groupedDeliveredClips = useMemo(
    () => groupClipsByMonth(deliveredDisplayClips),
    [deliveredDisplayClips]
  );
  const groupedSharedClips = useMemo(
    () => groupClipsByMonth(displaySharedClips),
    [displaySharedClips]
  );

  const loadJournals = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoadingJournals(true);
    try {
      const res = await fetch(`${API_BASE}/api/journals`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load journals");
      }
      setJournals(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setLocalError(err.message || "Could not load journals");
    } finally {
      setLoadingJournals(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadJournals();
  }, [loadJournals]);

  const loadClips = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoadingClips(true);
    try {
      const res = await fetch(`${API_BASE}/api/confessions`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load your private messages");
      }
      setClips(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setLocalError(err.message || "Could not load your private messages");
    } finally {
      setLoadingClips(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadClips();
  }, [loadClips]);

  const loadSharedWithMe = useCallback(async () => {
    if (!authToken) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/shared-with-me/messages`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load shared private messages");
      }
      setSharedWithMe(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setLocalError((current) => current || err.message || "Could not load shared private messages");
    }
  }, [authToken]);

  useEffect(() => {
    loadSharedWithMe();
  }, [loadSharedWithMe]);

  useEffect(() => {
    if (recorderError) {
      setLocalError(String(recorderError));
    }
  }, [recorderError]);

  useEffect(() => {
    const pending = [];
    let cancelled = false;
    displayClips.forEach((clip) => {
      if (!clip.viewUrl || clipDurations[clip.confessionId]) {
        return;
      }
      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = clip.viewUrl;
      const onLoadedMetadata = () => {
        if (!cancelled && Number.isFinite(video.duration)) {
          setClipDurations((current) => {
            if (current[clip.confessionId]) {
              return current;
            }
            return { ...current, [clip.confessionId]: video.duration };
          });
        }
      };
      video.addEventListener("loadedmetadata", onLoadedMetadata);
      pending.push({ video, onLoadedMetadata });
    });
    return () => {
      cancelled = true;
      pending.forEach(({ video, onLoadedMetadata }) => {
        video.removeEventListener("loadedmetadata", onLoadedMetadata);
        video.src = "";
      });
    };
  }, [clipDurations, displayClips]);

  const handleSaveClip = async () => {
    if (!mediaBlobUrl) {
      setLocalError("Record a clip before saving.");
      return;
    }
    if (!authToken) {
      setLocalError("Sign in again before saving this clip.");
      return;
    }
    setLocalError("");
    setSavingClip(true);
    try {
      const res = await fetch(mediaBlobUrl);
      const blob = await res.blob();
      const mimeType = blob.type || "video/webm";
      const contentSha256 = await sha256Hex(blob);
      const title = entryTitle.trim() || "Untitled private message";
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `${toSafeFileStem(title)}-${stamp}.webm`;
      const presRes = await fetch(`${API_BASE}/api/confessions/presign`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName,
          contentType: mimeType,
          contentSha256,
          sizeBytes: blob.size,
        }),
      });
      const pres = await presRes.json().catch(() => ({}));
      if (!presRes.ok) {
        throw new Error(pres.message || "Could not start your private message upload");
      }
      const putRes = await fetch(pres.uploadUrl, {
        method: "PUT",
        headers: pres.headers,
        body: blob,
      });
      if (!putRes.ok) {
        throw new Error(`Upload to storage failed (${putRes.status}).`);
      }
      const confRes = await fetch(`${API_BASE}/api/confessions/confirm`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          confessionId: pres.confessionId,
          s3Key: pres.s3Key,
          fileName,
          contentType: mimeType,
          contentSha256,
          title,
          recipientKey: selectedRecipient,
          linkedJournalId,
          contextNote: contextNote.trim(),
          recipientAccessAfterRelease: recipientAfterGone,
        }),
      });
      const saved = await confRes.json().catch(() => ({}));
      if (!confRes.ok) {
        throw new Error(saved.message || "Could not save your private message");
      }
      setUploadPlanNotice(
        `Saved to your account (${pres.planTier || "free"} upload limit: ${formatBytes(pres.maxBytes)}).`
      );
      clearBlobUrl();
      setEntryTitle("");
      setContextNote("");
      setLinkedJournalId("none");
      setRecipientAfterGone(true);
      await loadClips();
      await loadSharedWithMe();
    } catch (err) {
      setLocalError(err.message || "Could not save this private message. Please try again.");
    } finally {
      setSavingClip(false);
    }
  };

  const handleDiscardDraft = () => {
    clearBlobUrl();
    setLocalError("");
  };

  const handleRetryNotification = useCallback(
    async (clip) => {
      if (!clip?.confessionId || !authToken) {
        return;
      }
      setRetryingClipId(clip.confessionId);
      setLocalError("");
      try {
        const res = await fetch(`${API_BASE}/api/messages/${clip.confessionId}/retry-notification`, {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || "Could not retry the notification");
        }
        await loadClips();
      } catch (err) {
        setLocalError(err.message || "Could not retry the notification");
      } finally {
        setRetryingClipId("");
      }
    },
    [authToken, loadClips]
  );

  const renderMonthGroups = useCallback(
    (groups, buildRecipientLabel, options = {}) =>
      groups.map((monthGroup) => (
        <section key={`${options.prefix || "group"}-${monthGroup.monthKey}`} className="confession-month-group">
          <div className="confession-month-head">
            <h4>
              {monthGroup.monthKey} — {monthGroup.items.length}{" "}
              {monthGroup.items.length === 1 ? "message" : "messages"}
            </h4>
          </div>

          <ul className="confession-saved-grid">
            {monthGroup.items.map((clip) => (
              <li key={`${options.prefix || "clip"}-${clip.confessionId}`} className="confession-saved-card">
                <ConfessionCard
                  clip={clip}
                  durationLabel={clip.durationLabel}
                  recipientLabel={buildRecipientLabel(clip)}
                  onReview={setActiveClip}
                  onRetryNotification={options.retryable ? handleRetryNotification : null}
                  retryingNotification={retryingClipId === clip.confessionId}
                />
              </li>
            ))}
          </ul>
        </section>
      )),
    [handleRetryNotification, retryingClipId]
  );

  return (
    <section className="panel confession-workspace-panel">
      <div className="confession-workspace-head">
        <div>
          <p className="journal-section-kicker">Private messages</p>
          <h2>{copy.panelTitle}</h2>
          <p>{copy.panelSubtitle}</p>
        </div>
        <div className="confession-status-pills" aria-label="Private message status">
          <span className="journal-summary-pill">
            {clips.length} {clips.length === 1 ? "message" : "messages"}
          </span>
          <span className="journal-summary-pill">
            {savedThisYearCount ? `${savedThisYearCount} saved this year` : "No messages yet"}
          </span>
          <span className="journal-summary-pill">
            {isRecording ? "Recording now" : mediaBlobUrl ? "Draft ready to review" : "Ready to record"}
          </span>
          <span className="journal-summary-pill">Private by default</span>
        </div>
      </div>

      <div className="confession-layout">
        <aside className="confession-record-panel">
          <h3>Ready to record</h3>
          <LiveCameraPreview stream={previewStream} active={Boolean(previewStream)} />
          <div className="confession-recorder-actions">
            <button
              type="button"
              className={isRecording ? "confession-btn-recording" : ""}
              onClick={startRecording}
              disabled={isRecording || savingClip || isStarting || isStopping}
              aria-pressed={isRecording}
              aria-label={isRecording ? "Recording in progress" : "Start private message recording"}
            >
              {isRecording && <span className="confession-recording-dot" aria-hidden />}
              {startButtonLabel}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={stopRecording}
              disabled={savingClip || !isRecording}
              aria-label={isRecording ? "Stop recording" : "Stop (start recording first)"}
            >
              Stop
            </button>
            {mediaBlobUrl ? (
              <button type="button" className="secondary" onClick={handleDiscardDraft}>
                Discard take
              </button>
            ) : null}
          </div>
          {mediaBlobUrl ? (
            <div className="confession-draft-preview">
              <p className="helper-text">Review your latest message before saving it below.</p>
              <video src={mediaBlobUrl} controls className="confession-playback-video" />
            </div>
          ) : null}
          <div className="confession-trust-strip" aria-label="Private message trust cues">
            <span>🔒 Your videos are encrypted and private</span>
            <span>👨‍👩‍👧 Delivered only to selected recipients</span>
            <span>🕊️ Released based on your chosen conditions</span>
          </div>
        </aside>

        <div className="confession-meta-panel">
          <h3>Release details</h3>
          <label className="field-label" htmlFor="confession-title">
            Message title
          </label>
          <input
            id="confession-title"
            type="text"
            placeholder="Example: For my daughter on graduation day"
            value={entryTitle}
            onChange={(event) => setEntryTitle(event.target.value)}
          />

          <label className="field-label" htmlFor="confession-recipient">
            Recipient path
          </label>
          <select
            id="confession-recipient"
            value={selectedRecipient}
            onChange={(event) => setSelectedRecipient(event.target.value)}
          >
            <option value="all-family">All tagged family</option>
            {recipientOptions.map((option) => (
              <option key={option.id} value={String(option.backendNodeId || option.id)}>
                {option.name} ({option.relation})
              </option>
            ))}
          </select>

          <label className="field-label" htmlFor="confession-journal-link">
            Link to existing journal (optional)
          </label>
          <select
            id="confession-journal-link"
            value={linkedJournalId}
            onChange={(event) => setLinkedJournalId(event.target.value)}
            disabled={loadingJournals}
          >
            <option value="none">No linked journal</option>
            {journals.map((item) => (
              <option key={item.journalId} value={item.journalId}>
                {item.title || "Untitled entry"}
              </option>
            ))}
          </select>
          {linkedJournal ? (
            <p className="helper-text">
              Linked to: <strong>{linkedJournal.title || "Untitled entry"}</strong>
            </p>
          ) : null}

          <label className="field-label" htmlFor="confession-context-note">
            Context note (optional)
          </label>
          <textarea
            id="confession-context-note"
            rows={3}
            placeholder="Add context that will appear with the clip at release time."
            value={contextNote}
            onChange={(event) => setContextNote(event.target.value)}
          />

          <label className="confession-release-choice">
            <input
              type="checkbox"
              checked={recipientAfterGone}
              onChange={(event) => setRecipientAfterGone(event.target.checked)}
            />
            <span>
              Release this only after you are gone.
              <small>
                Keep it private until your release workflow is approved. Turn this off if you want
                the chosen recipient to receive it while you are still using AfterMe.
              </small>
            </span>
          </label>

          <p
            className={`confession-release-hint ${recipientAfterGone ? "confession-release-hint-delayed" : "confession-release-hint-immediate"}`}
          >
            {recipientAfterGone
              ? "This private message will stay private until your release checks are completed."
              : "This private message is marked to deliver now, without waiting for your release flow."}
          </p>

          <div className="confession-recorder-actions">
            <button type="button" onClick={handleSaveClip} disabled={!mediaBlobUrl || savingClip}>
              {savingClip ? "Saving..." : "Save private message"}
            </button>
            <button type="button" className="secondary" onClick={loadJournals} disabled={loadingJournals}>
              {copy.refreshJournals}
            </button>
            <button type="button" className="secondary" onClick={loadClips} disabled={loadingClips}>
              {copy.refreshSavedMessages}
            </button>
          </div>

          {localError ? <p className="error-message">{localError}</p> : null}
          {uploadPlanNotice ? <p className="helper-text">{uploadPlanNotice}</p> : null}
          <p className="helper-text">
            Your clips stay private and protected in encrypted storage. Save a message now, and
            keep it ready for the right person at the right time.
          </p>
        </div>
      </div>

      {loadingClips && !clips.length ? (
        <div className="confession-saved-list" aria-label="Loading saved private messages">
          <div className="confession-saved-head">
            <div>
              <h3>{copy.yourPrivateTitle}</h3>
              <p className="helper-text">Loading your saved messages…</p>
            </div>
          </div>
          <div className="confession-skeleton-grid" aria-hidden="true">
            {[0, 1, 2].map((item) => (
              <div key={item} className="confession-skeleton-card">
                <div className="confession-skeleton-thumb" />
                <div className="confession-skeleton-line confession-skeleton-line-strong" />
                <div className="confession-skeleton-line" />
                <div className="confession-skeleton-line confession-skeleton-line-short" />
              </div>
            ))}
          </div>
        </div>
      ) : privateDisplayClips.length || deliveredDisplayClips.length || displaySharedClips.length ? (
        <div className="confession-saved-list" aria-label="Saved private message archive">
          <div className="confession-saved-head">
            <div>
              <h3>{copy.yourPrivateTitle}</h3>
              <p className="helper-text">{copy.yourPrivateSubtitle}</p>
            </div>
            <div className="confession-archive-pills" aria-label="Private message archive summary">
              <span className="journal-summary-pill">
                {privateDisplayClips.length} {privateDisplayClips.length === 1 ? "message" : "messages"}
              </span>
              <span className="journal-summary-pill">
                {savedThisYearCount ? "Saved this year" : "Ready when you are"}
              </span>
              <span className="journal-summary-pill">Private by default</span>
            </div>
          </div>

          <p className="confession-archive-trust-note">
            Private messages stay private unless your chosen release rules allow sharing.
          </p>

          <div className="confession-archive-groups">
            {privateDisplayClips.length
              ? renderMonthGroups(groupedPrivateClips, (clip) =>
                  clip.recipientKey === "all-family"
                    ? "All tagged family"
                    : recipientLabelById.get(String(clip.recipientKey)) || "Specific family member"
                )
              : (
                <div className="confession-empty-subsection">
                  <p className="helper-text">
                    {isFrench
                      ? "Aucun message prive avec diffusion differee pour le moment."
                      : "No release-later private messages saved yet."}
                  </p>
                </div>
              )}
          </div>

          <div className="confession-saved-head confession-saved-head-subsection">
            <div>
              <h3>{copy.deliveredTitle}</h3>
              <p className="helper-text">{copy.deliveredSubtitle}</p>
            </div>
          </div>
          <div className="confession-archive-groups">
            {deliveredDisplayClips.length
              ? renderMonthGroups(
                  groupedDeliveredClips,
                  (clip) =>
                    clip.recipientKey === "all-family"
                      ? "All tagged family"
                      : recipientLabelById.get(String(clip.recipientKey)) || "Specific family member",
                  { prefix: "delivered", retryable: true }
                )
              : (
                <div className="confession-empty-subsection">
                  <p className="helper-text">
                    {isFrench
                      ? "Aucun message n’a encore ete partage immediatement."
                      : "You have not delivered any private messages yet."}
                  </p>
                </div>
              )}
          </div>

          <div className="confession-saved-head confession-saved-head-subsection">
            <div>
              <h3>{copy.sharedTitle}</h3>
              <p className="helper-text">{copy.sharedSubtitle}</p>
            </div>
          </div>
          <div className="confession-archive-groups">
            {displaySharedClips.length
              ? renderMonthGroups(
                  groupedSharedClips,
                  (clip) => clip.sharedByName || "Shared by someone",
                  { prefix: "shared" }
                )
              : (
                <div className="confession-empty-subsection">
                  <p className="helper-text">{copy.noShared}</p>
                </div>
              )}
          </div>
        </div>
      ) : (
        <div className="confession-empty-state">
          <div className="confession-empty-illustration" aria-hidden>
            💌
          </div>
          <h3>You haven’t recorded any private messages yet</h3>
          <p>Save a message when you are ready. You can choose a recipient and release timing later.</p>
          <button type="button" onClick={startRecording} disabled={isRecording || isStarting || isStopping}>
            Record your first message
          </button>
        </div>
      )}

      {activeClip ? (
        <ConfessionReviewModal
          clip={
            displayClips.find((item) => item.confessionId === activeClip.confessionId) ||
            displaySharedClips.find((item) => item.confessionId === activeClip.confessionId) ||
            activeClip
          }
          durationLabel={
            (
              displayClips.find((item) => item.confessionId === activeClip.confessionId) ||
              displaySharedClips.find((item) => item.confessionId === activeClip.confessionId) ||
              activeClip
            ).durationLabel
          }
          journalTitle={
            activeClip.linkedJournalId
              ? journals.find((item) => item.journalId === activeClip.linkedJournalId)?.title ||
                "Linked to a journal entry"
              : ""
          }
          recipientLabel={
            activeClip.isSharedWithMe
              ? activeClip.sharedByName || "Shared by someone"
              : activeClip.recipientKey === "all-family"
                ? "All tagged family"
                : recipientLabelById.get(String(activeClip.recipientKey)) || "Specific family member"
          }
          locale={locale}
          onClose={() => setActiveClip(null)}
          onOpenRelatedJournal={
            activeClip.linkedJournalId && onOpenLinkedJournal
              ? (journalId) => {
                  setActiveClip(null);
                  onOpenLinkedJournal(journalId);
                }
              : null
          }
        />
      ) : null}
    </section>
  );
}
